import { Injectable, WritableSignal, inject } from '@angular/core';
import { Sort } from '@angular/material/sort';
import { GridstackHeaderItem } from '@models/common';

import { SqlAggregateFunctionType } from '@models/helpers';
import { FieldTypeEnum } from '@models/module/fields/enums';
import { DataListAggregateFormulaFieldModel, DataListComplexFormulaFieldModel } from '@models/module/fields/main';
import { GridSearchEntityInstance } from '@models/module/grid';
import { DataListFields, GridFieldsModel } from '@models/module/main';
import { DataListCommandFilterType } from '@shared/components/data-list/common/models/data-list-command.interface';
import {
  ActionType,
  CipoTableOptions,
  DisplayFormats,
  FooterRow,
  TableColumn,
  TableRow,
} from '@shared/components/data-list/common/models/table.interface';
import { LocalFilterService } from '@shared/components/data-list/common/services/local-filter.service';
import { AppColors } from '@shared/consts';
import { DropdownItem } from '@shared/consts/dropdown.consts';
import { BREAKPOINTS } from 'src/app-breakpoints.module';

export type EntityTableRow = TableRow & {
  [Property in keyof GridSearchEntityInstance as Exclude<Property, 'state_name'>]: GridSearchEntityInstance[Property];
};

type DropdownAction = Pick<ActionType, 'id' | 'displayName' | 'leftIcon' | 'rightIcon'>;

@Injectable({
  providedIn: 'root',
})
export class TableDataService {
  private localFiltersService = inject(LocalFilterService);

  private getDisplayFormatId({ typeId, systemName }: DataListFields): DisplayFormats {
    if (systemName === 'state_name') {
      return DisplayFormats.Status;
    }
    switch (typeId) {
      case FieldTypeEnum.Attachment:
        return DisplayFormats.Image;
      case FieldTypeEnum.YesNo:
        return DisplayFormats.YesNo;
      case FieldTypeEnum.Date:
        return DisplayFormats.Date;
      case FieldTypeEnum.Number:
        return DisplayFormats.Number;
      default:
        return DisplayFormats.Text;
    }
  }

  getTableColumns(fields: DataListFields[]): TableColumn[] {
    return (fields ?? [])
      .filter(field => !!field.position)
      .map(field => {
        return {
          id: field.id,
          name: this.getFieldName(field),
          displayFormatId: this.getDisplayFormatId(field),
          displayName: field.label,
          formattings: field.formattings,
          restrictions: field.restrictions,
          priority: field.priority,
          sortable: true,
          // for now,
          align: ['state_name'].includes(field.name) ? 'center' : null,
          width: field.width,
          systemName: field.systemName,
          showThumbnail: field.showThumbnail,
          showColorCode: field.showColorCode,
          isPrimarySort: field.isPrimarySort,
          isDescending: field.isDescending,
        };
      });
  }

  getFieldName(field: DataListFields | TableColumn): string {
    return field.id > 0 || field.isContractSystemField ? field.id.toString() : field.name;
  }

  /**
   * Maps the top formula fields from a GridFieldsModel to an array of GridstackHeaderItem.
   * Each formula field is transformed to include width (w) and height (h) properties based on its cols and rows.
   * @param topFormulasFields - The top formula fields to map
   * @returns GridstackHeaderItem[] - The mapped gridstack header items
   */
  getTopFormulaFields(topFormulasFields: DataListComplexFormulaFieldModel[]): GridstackHeaderItem[] {
    return (topFormulasFields ?? []).map(field => ({
      ...field,
      w: field.cols,
      h: field.rows,
    }));
  }

  /**
   * Map bottom formulas fields to footer rows. Used for dynamic modules, for now.
   * @param bottomFormulasFields - The bottom formulas fields to map
   * @returns FooterRow[] - The mapped footer rows
   */
  getFooterRows(bottomFormulasFields: DataListAggregateFormulaFieldModel[]): FooterRow[] {
    const footerRows = (bottomFormulasFields ?? []).reduce<FooterRow[]>((acc, formula) => {
      // Find or create the group for the current operator
      let group = acc.find(item => item.key === formula.operator);
      if (!group) {
        group = {
          key: formula.operator,
          label: SqlAggregateFunctionType[formula.operator],
          empty: true,
        };
        acc.push(group);
      }

      return acc;
    }, []);
    return footerRows;
  }

  mapTableRowsState(rows: GridSearchEntityInstance[], markedFieldName?: string): EntityTableRow[] {
    return rows.map(row => ({
      ...row,
      state_name: this.mapState(row),
      ...(markedFieldName && row[markedFieldName] && { backgroundColor: AppColors.accent_100 }),
    }));
  }

  mapState(
    row: Pick<
      GridSearchEntityInstance,
      'entity_template_id' | 'needs_approval' | 'state_color' | 'state_text_color' | 'state_name'
    >,
  ): { bgColor: string; color: string; label: string } {
    if (row.entity_template_id) {
      return {
        bgColor: '#8cd9c9',
        color: '#080000',
        label: 'dm.fixedStates.Preset',
      };
    }
    if (row.needs_approval) {
      return {
        bgColor: '#ffab00',
        color: '#ffffff',
        label: 'dm.fixedStates.inApproval',
      };
    }
    return {
      bgColor: row.state_color,
      color: row.state_text_color,
      label: row.state_name,
    };
  }

  /**
   * Maps a `DropdownItem` to a `DropdownAction` by setting the appropriate label, id and icon.
   *
   * @param {DropdownItem} param0 - The dropdown item containing the icon, id, and label.
   * @param {'Left' | 'Right'} [iconPosition='Right'] - The position of the icon, either 'Left' or 'Right'. Defaults to 'Right'.
   * @returns {DropdownAction} The mapped dropdown action with the icon positioned accordingly.
   */
  mapRowAction({ icon, id, label }: DropdownItem, iconPosition: 'Left' | 'Right' = 'Left'): DropdownAction {
    return {
      id,
      displayName: label,
      leftIcon: iconPosition === 'Left' ? icon : undefined,
      rightIcon: iconPosition === 'Right' ? icon : undefined,
    };
  }

  /**
   * Updates the pagination length in the provided CipoTableOptions signal.
   * @param options - A WritableSignal containing the CipoTableOptions to be updated.
   * @param length - The new length value to set in the pagination options.
   */
  updatePaginationLength(tableOptionsSignal: WritableSignal<CipoTableOptions>, length: number) {
    // spread used because data lists components listen for options changes
    tableOptionsSignal.update(options => {
      options.pagination.length = length;
      return { ...options };
    });
  }

  /**
   * Resets the sort options in the provided CipoTableOptions signal to null.
   * @param options - A WritableSignal containing the CipoTableOptions to be updated.
   * @param reset - A boolean indicating whether to reset the sort options. Defaults to true.
   */
  resetSort(tableOptionsSignal: WritableSignal<CipoTableOptions>, reset: boolean = true) {
    if (reset) {
      tableOptionsSignal.update(options => {
        options.sort = null;
        return options;
      });
    }
  }

  /**
   * Sets the default sort options in the provided CipoTableOptions signal based on the primary sort field in GridFieldsModel.
   * If no primary sort field is found, it defaults to sorting by 'full_doc_num' in descending order.
   * @param tableOptionsSignal - A WritableSignal containing the CipoTableOptions to be updated.
   * @param gridFields - The GridFieldsModel containing the fields to determine the primary sort field.
   */
  setDefaultSort(tableOptionsSignal: WritableSignal<CipoTableOptions>, gridFields: GridFieldsModel) {
    this.setDefaultSortOnColumns(tableOptionsSignal, gridFields.fields);
  }

  /**
   * Sets the default sort options in the provided CipoTableOptions signal based on the primary sort field in GridFieldsModel.
   * If no primary sort field is found, it defaults to sorting by 'full_doc_num' in descending order.
   * @param tableOptionsSignal - A WritableSignal containing the CipoTableOptions to be updated.
   * @param gridFields - The GridFieldsModel containing the fields to determine the primary sort field.
   */
  setDefaultSortOnColumns(
    tableOptionsSignal: WritableSignal<CipoTableOptions>,
    columns: (DataListFields | TableColumn)[],
  ) {
    const sort = this.getDefaultSortOnColumns(columns);
    if (!sort) {
      return;
    }

    tableOptionsSignal.update(options => {
      options.sort = { ...sort };
      return options;
    });
  }

  getDefaultSortOnColumns(columns: (DataListFields | TableColumn)[]): Sort {
    if (!columns.length) {
      return null;
    }
    const primarySortField = columns.find(field => field.isPrimarySort);
    let sortField = 'full_doc_num';
    if (!!primarySortField) {
      sortField = this.getFieldName(primarySortField);
    } else if (!columns.some(f => f.name === 'full_doc_num')) {
      // in case of GridFields, we need to filter the fields that are filters because they are not on the datalist and will throw an error if we try to sort by them because they don't exists in GridSearch
      const filterField = columns.filter(c => !c.isFilter)[0];
      if (filterField) {
        sortField = this.getFieldName(filterField);
      }
    }
    return {
      active: sortField,
      // this handles the case when the primarySort is not set, it will sort by full_doc_num, descending
      direction: primarySortField?.isDescending === false ? 'asc' : 'desc',
    };
  }

  /**
   * Sets various grid-related fields and configurations based on the provided GridFieldsModel.
   * This includes setting table columns, top and bottom formulas, table options, and filters.
   * @param gridFields - The GridFieldsModel containing the fields and formulas to be set.
   * @param tableColumnsSignal - A WritableSignal to set the table columns.
   * @param topFormulasSignal - A WritableSignal to set the top formula fields.
   * @param bottomFormulasSignal - A WritableSignal to set the bottom formula fields.
   * @param tableOptionsSignal - A WritableSignal to set the table options.
   * @param filtersSignal - A WritableSignal to set the data list command filters.
   * @param setDefaultSort - A boolean indicating whether to set the default sort options.
   */
  setGridFields(
    gridFields: GridFieldsModel,
    tableColumnsSignal: WritableSignal<TableColumn[]>,
    topFormulasSignal: WritableSignal<GridstackHeaderItem[]>,
    bottomFormulasSignal: WritableSignal<FooterRow[]>,
    tableOptionsSignal: WritableSignal<CipoTableOptions>,
    filtersSignal: WritableSignal<DataListCommandFilterType[]>,
    setDefaultSort: boolean,
  ) {
    // set table columns
    if (tableColumnsSignal) {
      tableColumnsSignal.set(this.getTableColumns(gridFields?.fields));
    }

    // set default sort
    if (setDefaultSort && tableOptionsSignal) {
      this.setDefaultSort(tableOptionsSignal, gridFields);
    }

    // set top and bottom formulas if not smallscreen
    const isSmallscreen = window.innerWidth < BREAKPOINTS.sm;
    if (!isSmallscreen) {
      if (topFormulasSignal) {
        topFormulasSignal.set(this.getTopFormulaFields(gridFields?.topFormulas));
      }
      if (bottomFormulasSignal) {
        bottomFormulasSignal.set(this.getFooterRows(gridFields?.bottomFormulas));
      }
    }

    if (filtersSignal) {
      filtersSignal.set(
        this.localFiltersService.mapFieldsToFilters((gridFields?.fields ?? []).filter(f => f.isFilter || f.position)),
      );
    }
  }
}
